import axios from 'axios';

const POLL_MS                = 1500;
const MAX_CONSECUTIVE_404S   = 20;           // ~30 s grace window, then terminal
const MAX_CONSECUTIVE_ERRORS = 10;           // ~15 s grace window for transient network/5xx errors

// No hard duration cap: we poll until the backend reports COMPLETE/FAILED so a
// long job (e.g. a big building code) is never abandoned mid-flight. To avoid
// hammering the status endpoint over a 20+ minute run, the interval backs off
// the longer the job has been running.
function pollDelayFor(elapsedMs) {
  if (elapsedMs < 60 * 1000)      return POLL_MS;   // first minute: snappy
  if (elapsedMs < 5 * 60 * 1000)  return 5000;      // 1–5 min: every 5s
  return 15000;                                      // 5 min+: every 15s
}

// --- pub/sub for header indicator (singleton only) ---
let currentState = null;
const listeners  = new Set();
const notify     = () => listeners.forEach(fn => fn(currentState));

export function subscribe(listener) {
  listeners.add(listener);
  listener(currentState); // push current value immediately
  return () => listeners.delete(listener);
}

const prettify = (row, startTs) => {
  const s = row?.stage || 'WAITING';
  const e = row?.extra || {};
  const stageText = {
    INITIALIZING:        'Starting…',
    CHECKING_OPENSEARCH: 'Connecting to search…',
    DOWNLOADING:         'Downloading extracted text…',
    CHUNKING:            'Splitting document…',
    EMBEDDING:           `Embedding chunks (${e.currentEmbeddingBatch || 0}/${e.totalEmbeddingBatches || 0})…`,
    INDEXING:            `Indexing (${e.currentIndexBatch || 0}/${e.totalIndexBatches || 0})…`,
    REFRESHING:          'Refreshing index…',
    COMPLETE:            'Done',
    FAILED:              'Failed',
    WAITING:             'Waiting for extraction to finish…',
  };
  const message  = row?.message || stageText[s] || 'Processing…';
  const progress = typeof row?.progress === 'number'
    ? row.progress
    : Math.min(15, Math.round((Date.now() - startTs) / 1000) * 2);
  return { ...row, message, progress };
};

/**
 * Creates an independent poller instance.
 * Use one per concurrent job — no shared timer/state between instances.
 * The default export singleton is reserved for the header progress indicator.
 */
export function createPoller() {
  let timer             = null;
  let startTs           = 0;
  let consecutive404s   = 0;
  let consecutiveErrors = 0;
  let stopped           = false;

  function stopPolling() {
    stopped = true;
    if (timer) { clearTimeout(timer); timer = null; }
  }

  function startPolling({ jobId, statusUrl, onUpdate, onDone, onError }) {
    stopPolling();          // cancel any previous poll on this instance
    stopped           = false;
    startTs           = Date.now();
    consecutive404s   = 0;
    consecutiveErrors = 0;

    // Schedule the next poll with a backed-off delay. Self-scheduling
    // (setTimeout, not setInterval) guarantees we never overlap requests and
    // lets the cadence slow down as a job runs long.
    const scheduleNext = () => {
      if (stopped) return;
      timer = setTimeout(poll, pollDelayFor(Date.now() - startTs));
    };

    const poll = async () => {
      if (stopped) return;

      try {
        const res   = await axios.get(statusUrl, { params: { jobId } });
        const row   = res.data || {};
        consecutive404s   = 0; // reset on any successful response
        consecutiveErrors = 0;
        const state = prettify(row, startTs);
        onUpdate?.(state);

        if (row.status === 'COMPLETE' || row.stage === 'COMPLETE') {
          stopPolling();
          onDone?.(state);
          return;
        }
        if (row.status === 'FAILED' || row.stage === 'FAILED') {
          // The only case that's a confirmed backend failure — everything
          // else below is "we lost the connection," not "it definitely failed."
          stopPolling();
          onError?.({ ...state, code: 'FAILED' });
          return;
        }
        // Still in-progress — keep polling.
        scheduleNext();
      } catch (err) {
        if (err?.response?.status === 404) {
          consecutive404s++;
          if (consecutive404s >= MAX_CONSECUTIVE_404S) {
            // Job record never appeared or was cleaned up — terminal, but
            // not a confirmed failure (the job may still be running server-side).
            stopPolling();
            onError?.({ message: 'Job record not found after grace period', code: 404 });
            return;
          }
          // Within grace window — show waiting state but keep polling
          const waiting = prettify({ stage: 'WAITING' }, startTs);
          onUpdate?.(waiting);
          scheduleNext();
          return;
        }

        // Non-404 error (network, 5xx, etc.) — retry a few times before
        // giving up. One flaky request shouldn't end multi-minute tracking.
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          stopPolling();
          onError?.({ message: err?.message || 'Polling failed', code: 'NETWORK' });
          return;
        }
        // Within grace window — keep polling, no state change needed.
        scheduleNext();
      }
    };

    poll(); // immediate first call; each call schedules the next
  }

  return { startPolling, stopPolling };
}

// ── Singleton — for the header progress indicator via subscribe() ──────────
// Wraps createPoller() and wires onUpdate/onDone/onError into the pub/sub.
const _singleton = createPoller();

function startPolling(opts) {
  const { onUpdate, onDone, onError } = opts;
  _singleton.startPolling({
    ...opts,
    onUpdate: (state) => {
      currentState = state;
      notify();
      onUpdate?.(state);
    },
    onDone: (state) => {
      currentState = null;
      notify();
      onDone?.(state);
    },
    onError: (err) => {
      currentState = null;
      notify();
      onError?.(err);
    },
  });
}

function stopPolling() {
  _singleton.stopPolling();
  currentState = null;
  notify();
}

const documentChunkingProgressService = { startPolling, stopPolling, subscribe };
export default documentChunkingProgressService;